import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { apiClient } from '../../apiClient'

export const ORG_LIST_KEY = 'org-list'
export const ORG_ELIGIBLE_MEMBERS_LIST_KEY = 'org-eligible_members-list'
export const ORG_TOKEN_ALERT_SUMMARY_KEY = 'org-token-alert-summary'

type Role =
  | 'admin'
  | 'bot'
  | 'customer'
  | 'platform_user'
  | 'account_owner'
  | 'operator'
  | 'trainee'
  | 'applicant'
  | 'workforce_manager'

type Status = 'active' | 'invited'

type MemberAlert = 'tokens_to_review'

type OrganizationMemberRequest = {
  status?: Status
  roles?: Role[]
  alert?: MemberAlert
  appUserId?: string
  orgId?: string
  cursor: number
  limit: number
  orderBy: string
  sortDescending: boolean
  includeTokenAlerts?: boolean
}

type MembersData = {
  id: string
  name: string
  email?: string
  avatarUrl?: string
  status: Status
  createdAt?: string
  role: Role
  tokensCount: number
  expiringSoonTokensCount?: number
  expiredTokensCount?: number
  allowance: number
  usage: number
}

type OrganizationMemberResponse = {
  total: number
  members: MembersData[]
}

type TokenAlertSummaryResponse = {
  expiringSoonTokens: number
  expiredTokens: number
}

type OrganizationMembersUpdateRequestPayload = {
  orgId: string
  role: Role
  userIds: string[]
}

type OrganizationMembersDeleteRequestPayload = {
  orgId: string
  userIds: string[]
}
type OrganizationMembersDeleteInviteRequestPayload = {
  orgId: string
  inviteIds: string[]
}

type EligibleMember = {
  id: string
  name: string
  email: string
  avatarUrl?: string
}

type OrganizationMembersEligibleResponse = {
  eligibleUsers: EligibleMember[]
  currentMembers: EligibleMember[]
}

type OrganizationMembersInvitePayload = {
  orgId: string
  role: Role
  emails: string[]
}

type OrganizationBotsPayload = {
  orgId: string
  botNames: string[]
}

export function useGetList({ orgId, ...params }: OrganizationMemberRequest) {
  return useQuery({
    queryKey: [ORG_LIST_KEY, orgId, params],
    queryFn: async () => {
      const { data } = await apiClient.get<OrganizationMemberResponse>(
        `/int/v3/organizations/${orgId}/members`,
        {
          params,
        }
      )

      return data
    },
    refetchOnWindowFocus: false,
    enabled: Boolean(orgId),
  })
}

export function useGetTokenAlertSummary(orgId?: string) {
  return useQuery({
    queryKey: [ORG_TOKEN_ALERT_SUMMARY_KEY, orgId],
    queryFn: async () => {
      const { data } = await apiClient.get<TokenAlertSummaryResponse>(
        `/int/v3/organizations/${orgId}/token-alerts/summary`
      )

      return data
    },
    refetchOnWindowFocus: false,
    enabled: Boolean(orgId),
  })
}

export function useInvalidateTokenAlertSummary() {
  const queryClient = useQueryClient()
  return useCallback(
    (orgId?: string) => queryClient.invalidateQueries({ queryKey: [ORG_TOKEN_ALERT_SUMMARY_KEY, orgId] }),
    [queryClient]
  )
}

export function useGetEligibleList(orgId?: string, withBots = false) {
  return useQuery({
    queryKey: [ORG_ELIGIBLE_MEMBERS_LIST_KEY, orgId, withBots],
    queryFn: async () => {
      const { data } = await apiClient.get<OrganizationMembersEligibleResponse>(
        `/int/v3/organizations/${orgId}/members/eligible`,
        {
          params: {
            withBots,
          },
        }
      )

      return data
    },
    refetchOnWindowFocus: false,
    enabled: Boolean(orgId),
  })
}

export function useUpdate() {
  return useMutation({
    mutationFn: async ({ orgId, ...payload }: OrganizationMembersUpdateRequestPayload) => {
      return await apiClient.put(`/int/v3/organizations/${orgId}/members`, payload)
    },
  })
}

export function useDelete() {
  return useMutation({
    mutationFn: async ({ orgId, ...payload }: OrganizationMembersDeleteRequestPayload) => {
      return await apiClient.delete(`/int/v3/organizations/${orgId}/members`, { data: payload })
    },
  })
}

export function useDeleteInvite() {
  return useMutation({
    mutationFn: async ({ orgId, ...payload }: OrganizationMembersDeleteInviteRequestPayload) => {
      return await apiClient.delete(`/int/v3/organizations/${orgId}/members/invite`, { data: payload })
    },
  })
}

export function useInvite() {
  return useMutation({
    mutationFn: async ({ orgId, ...payload }: OrganizationMembersInvitePayload) => {
      return await apiClient.post(`/int/v3/organizations/${orgId}/members/invite`, payload)
    },
  })
}

export function useCreateBots() {
  return useMutation({
    mutationFn: async ({ orgId, ...payload }: OrganizationBotsPayload) => {
      return await apiClient.post(`/int/v3/organizations/${orgId}/bots`, payload)
    },
  })
}
